import * as PIXI from "pixi.js";
import anime from "animejs";
import { symbolData } from "../../../SymbolData";
import {
  getSymbolNativeDimensions,
  formatToCustomLocale,
} from "../../../utilities";
import {
  Settings,
} from "../../../settings";
import {
  COIN_SYMBOL_IDS,
  COIN_GENERATION_PROBABILITY,
  WILD_SYMBOL_ID,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { setSpinComplete } from "../../../State/actions";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = Settings.SYMBOLS_VISIBLE * 2 + 2;
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
// Visible symbols start after top buffer (index 1) + landing zone (N symbols)
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE_SYMBOLS = Settings.SYMBOLS_VISIBLE;

// Cache available symbol IDs once (module-level constant for O(1) performance)
// Excludes ID 0 (blank) and ID 10 (blank coin)
const AVAILABLE_SYMBOL_IDS = (() => {
  const ids = [];
  for (let id = 1; id <= 9; id++) {
    if (symbolData[id]?.texture) {
      ids.push(id);
    }
  }
  return ids;
})();

// Safari-specific precision factor (Safari doesn't need as much precision)
const PRECISION_FACTOR = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent
)
  ? 10000
  : 100000;

// Main game reel animation controller - handles wind-up, spin, stop animations with sprite wrapping
class ReelControllerV2 {
  constructor(
    dispatch,
    spinningContainer,
    spriteSheet,
    spriteSheetBlurred,
    // multiplierSpriteSheet, // TODO: Add multiplier support later
    reels,
    reelIndex,
    symbolHeight,
    onReelStop = null, // Callback when reel stops (for landing animations)
    autoPlaySpeed = "auto", // Current autoplay speed mode
    bet = 1 // Current bet value for coin display
  ) {
    this.dispatch = dispatch;
    this.spinningContainer = spinningContainer;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.spritesheetBlurred = spriteSheetBlurred;
    this.symbolHeight = symbolHeight;
    this.symbolWidth = symbolHeight; // Assume square for now
    this.reels = reels || []; // Store API landing data (array of symbol IDs)
    this.hasPendingLandingSymbols = false; // Flag to indicate API data is ready
    this.onReelStop = onReelStop; // Callback for landing animations
    this.autoPlaySpeed = autoPlaySpeed; // Current speed setting
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.bet = bet || 1; // Store bet value for coin calculations

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isTensionMode = false;
    this.tickerCallback = null;
    this.spinningSprites = [];
    this.spinStartTime = null;
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null; // Deprecated: use this.reels array instead
    this.isBlurred = false;
    this.blurTweenRefs = { normal: null, blurred: null };
    this.visibleSprites = [];
    this.nativeDimensions = null;
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    this.speedMultiplier = 1.0; // Normal speed = 1.0, Tension = 0.333 (3x slower)
    this.hasWinThisSpin = false; // Track if this spin has a win (affects sound playback)
    this.isHoldAndWinTrigger = false; // Track if this spin triggers Hold & Win (6+ coins)
    this.pendingTimeouts = []; // Track setTimeout calls for cleanup

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      spriteSheet?.data?.frames || spriteSheet?.textures || null;

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
  }

  // Picks a random symbol from available symbols (O(1) direct random)
  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) {
      return 1; // fallback
    }
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * this.bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // Receives new reel data from API - used for landing symbols when reel stops
  updateReels(newReels) {
    if (newReels && Array.isArray(newReels)) {
      this.reels = newReels;
      this.hasPendingLandingSymbols = true;
    }
  }

  // Updates bet value - coin values will update automatically during next landing sequence
  updateBet(newBet) {
    this.bet = newBet || 1;
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo || this.spinningSprites.length === 0) return;

    const visibleSpriteIndex = this.spinningSprites.length - 1;
    this._setSpriteTexture(this.spinningSprites[visibleSpriteIndex], symbolId);
    this.symbolId = symbolId;
  }

  // Creates spinning sprites with wrapping
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      normalSprite.cacheAsBitmap = false;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      blurredSprite.cacheAsBitmap = false;
      symbolContainer.addChild(blurredSprite);

      const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      const valueText = new PIXI.BitmapText("", {
        fontName: "font",
        fontSize: fontSize,
      });
      valueText.anchor.set(0.5);
      valueText.name = "symbol_value";
      valueText.zIndex = 1;
      valueText.alpha = 0;
      valueText.roundPixels = true;
      symbolContainer.addChild(valueText);

      const valueTextBlurred = new PIXI.BitmapText("", {
        fontName: "font_blurred",
        fontSize: fontSize,
      });
      valueTextBlurred.anchor.set(0.5);
      valueTextBlurred.name = "symbol_value_blurred";
      valueTextBlurred.zIndex = 1;
      valueTextBlurred.alpha = 0;
      valueTextBlurred.roundPixels = true;
      symbolContainer.addChild(valueTextBlurred);

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this.hasInitializedTextures = true;
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    this.visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);
  }

  // Calculates symbol scale to fit container (maintains aspect ratio)
  _calculateSymbolScale(symbolName, containerWidth, containerHeight) {
    if (!this.reelSymbolsData || !symbolName) return 1;

    if (!this.nativeDimensions) {
      const native = getSymbolNativeDimensions(symbolName, {
        frames: this.reelSymbolsData,
      });
      if (!native) return 1;
      this.nativeDimensions = native;
    }

    const { width: nativeWidth, height: nativeHeight } = this.nativeDimensions;
    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    return Math.min(widthScale, heightScale);
  }

  // Sets sprite textures (normal/blurred) and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    if (spriteData.currentSymbolId === symbolId) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolName = symbolInfo.name;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] || PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0.001 : 1;

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0.001;
      }
    }

    if (symbolName) {
      const scale = this._calculateSymbolScale(
        symbolName,
        this.symbolWidth,
        this.symbolHeight
      );
      spriteData.normalSprite.scale.set(scale);
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.scale.set(scale);
      }
    }

    // Update coin value text (both normal and blurred)
    if (spriteData.valueText) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y = 0;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // Only apply scaling if text is too wide
        if (actualTextWidth > maxTextWidth) {
          // Count digits (excluding formatting characters like commas and dots)
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;

          // Apply scaling method based on digit count
          if (digitCount <= 4) {
            // 1-4 digits: Horizontal squishing only
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else if (digitCount >= 5 && digitCount <= 8) {
            // 5-8 digits: Horizontal squishing only (preserve height)
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else {
            // 9+ digits: Uniform scaling (both width and height)
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.set(scale);
          }
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y = 0;

        // Apply same scaling logic to blurred text
        if (displayValue !== "") {
          // Reset scale first
          spriteData.valueTextBlurred.scale.set(1);

          // Force update to get accurate measurements
          spriteData.valueTextBlurred.updateText();

          // Calculate available width (with padding)
          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1; // 10% padding on each side
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // Only apply scaling if text is too wide
          if (actualTextWidth > maxTextWidth) {
            // Count digits (excluding formatting characters like commas and dots)
            const digitCount = displayValue.replace(/[^0-9]/g, '').length;

            // Apply scaling method based on digit count
            if (digitCount <= 4) {
              // 1-4 digits: Horizontal squishing only
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else if (digitCount >= 5 && digitCount <= 8) {
              // 5-8 digits: Horizontal squishing only (preserve height)
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else {
              // 9+ digits: Uniform scaling (both width and height)
              const scale = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.set(scale);
            }
          }
        }
      }
    }
  }

  // Generates random symbol - uses boosted coin probability in tension mode
  _generateRandomSymbolId() {
    const coinProbability = this.isTensionMode
      ? FEATURES_CONFIG.TENSION.COIN_GENERATION_PROBABILITY
      : COIN_GENERATION_PROBABILITY;

    if (Math.random() < coinProbability) {
      return COIN_SYMBOL_IDS[
        Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
      ];
    } else {
      return this._pickRandomSymbolId();
    }
  }

  // Initializes textures - visible sprites use API data, buffers use random symbols
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // Check if we have initial reels data from API
    const hasInitialReels =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;

      if (
        hasInitialReels &&
        i >= VISIBLE_SYMBOL_START_INDEX &&
        i < VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        const reelIndex = i - VISIBLE_SYMBOL_START_INDEX;
        symbolId = this.reels[reelIndex];
      } else {
        symbolId = this._generateRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
  }

  // Checks if landing zone sprites are visible - determines when to apply API landing symbols
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const containerY = this.spinningContainer.y;
    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    // Check if all landing zone sprites (1-4) are within visible bounds
    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) return false;

      const absoluteY = containerY + sprite.container.y;
      if (absoluteY < visibleTop || absoluteY > visibleBottom) {
        return false;
      }
    }
    return true;
  }

  // Applies API landing symbols to landing zone - sprites drop into visible area on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    if (this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          const apiIndex = i - 1;
          const symbolIdForThisPosition = this.reels[apiIndex];

          if (
            symbolIdForThisPosition !== undefined &&
            symbolData[symbolIdForThisPosition]
          ) {
            this._setSpriteTexture(
              this.spinningSprites[i],
              symbolIdForThisPosition
            );
          }
        }
      }
    } else if (symbolId !== null) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          this._setSpriteTexture(this.spinningSprites[i], symbolId);
        }
      }
    }
  }

  // Start spin with wind-up animation
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.spinningSprites.forEach((spriteData) => {
      spriteData.normalSprite.cacheAsBitmap = false;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.cacheAsBitmap = false;
      }
    });

    const windUpDuration = FEATURES_CONFIG.SPIN.WIND_UP_DURATION;
    if (windUpDuration === 0) {
      this._startSpinAnimation();
      return;
    }

    this.isWindingUp = true;
    const windUpDistance = -0.5 * this.symbolHeight;

    const currentY = this.spinningContainer.y;
    const originalY = 0;

    if (Math.abs(currentY) > 0.001) {
      this.spinningContainer.y = originalY;
    }

    this.windUpAnimation = anime({
      targets: this.spinningContainer,
      y: originalY + windUpDistance,
      duration: windUpDuration,
      easing: "easeOutCubic",
      complete: () => {
        this.spinningContainer.y = originalY;
        this.isWindingUp = false;
        this.windUpAnimation = null;
        this._startSpinAnimation();
      },
    });
  }

  // Start turbo spin (no wind-up, no animation, instant setup)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    this.hasStopped = false;
    this.isStopping = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.spinStartTime = performance.now();

    this._switchToNormalTextures();

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this.spinningContainer.y = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.spinningSprites.forEach((spriteData) => {
      spriteData.normalSprite.cacheAsBitmap = false;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.cacheAsBitmap = false;
      }
    });

    this.isPlaying = true;
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.isPlaying = false;
    this.stopTweenActive = true;

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const apiIndex = i - 1;
      const symbolId = this.reels[apiIndex];
      if (symbolId !== undefined) {
        this._setSpriteTexture(this.spinningSprites[i], symbolId);
      }
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
    const dropDuration = turboConfig.DROP_DURATION || 300;
    const bounceOvershoot = turboConfig.BOUNCE_OVERSHOOT || 1.5;

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        for (let w = 0; w < STOP_MOVE_DISTANCE_SYMBOLS; w++) {
          const movedSprite = this.spinningSprites.pop();
          this.spinningSprites.unshift(movedSprite);
        }

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }

        // Set critical state flags immediately
        this.spinningContainer.y = 0;
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }

        // Heavy operations AFTER landing animations start (non-blocking for animations)
        this.spinningSprites.forEach((spriteData) => {
          spriteData.normalSprite.cacheAsBitmap = true;
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.cacheAsBitmap = true;
          }
        });

        // Play sound when animation completes
        if (this.reelIndex === Settings.REEL_COUNT - 1 && !this.hasWinThisSpin) {
          soundManager.play(this._getTurboModeSound());
        }

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          // Add delay for non-winning spins to prevent spam
          // Winning spins and Hold & Win triggers are protected by their respective animations
          if (this.hasWinThisSpin || this.isHoldAndWinTrigger) {
            this.dispatch(setSpinComplete(true));
          } else {
            const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;
            const isTurbo = effectiveSpeed?.toLowerCase() === 'turbo';
            const delay = isTurbo
              ? FEATURES_CONFIG.SPIN.SPIN_COMPLETE_DELAY.TURBO
              : FEATURES_CONFIG.SPIN.SPIN_COMPLETE_DELAY.REGULAR;

            const timeoutId = setTimeout(() => {
              // Remove from tracking array when it fires
              const idx = this.pendingTimeouts.indexOf(timeoutId);
              if (idx > -1) this.pendingTimeouts.splice(idx, 1);

              this.dispatch(setSpinComplete(true));
            }, delay);

            // Track timeout for cleanup
            this.pendingTimeouts.push(timeoutId);
          }
        }
      },
    });
  }

  // Starts spin animation after wind-up completes
  _startSpinAnimation() {
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.isBlurred = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;

    // Start reel_spin sound when first reel begins actual spinning (after wind-up)
    if (this.reelIndex === 0) {
      soundManager.play(SOUND_NAME.reel_spin);
    }

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = performance.now();
    this.lastCalculatedPosition = 0;

    const blurDuration = Math.round(speedConfig.SPIN_DURATION * 0.5);
    this._switchToBlurredTextures(blurDuration);

    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // Initiates stop sequence - cannot stop during wind-up
  stopSpin() {
    if (this.isWindingUp) {
      return;
    }

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // Tension mode - adjusts speed multiplier without restarting animation
  spinTension() {
    if (this.isTensionMode) return;

    if (this.isPlaying && !this.isStopping) {
      this.isTensionMode = true;
      this.lockedSpinSpeed = this.autoPlaySpeed;
      this.speedMultiplier = FEATURES_CONFIG.TENSION.SPEED_MULTIPLIER;
      return;
    }
  }

  // Stops tension mode spin
  stopSpinTension() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
    this.isStopping = true;
  }

  // Main animation loop
  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) {
      return;
    }

    const elapsedTime = (performance.now() - this.spinStartTime) / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition;
    movement *= this.speedMultiplier;

    const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const symbol = this.spinningSprites[i];
      symbol.container.y += movement;

      if (symbol.container.y >= wrapThreshold) {
        const excessDistance = symbol.container.y - wrapThreshold;
        symbol.container.y = excessDistance;

        if (this._pendingPositionUpdate) {
          this._applyPendingResize();
        }

        this.moveLastSymbolOnTop();

        if (
          this.pendingLandingSymbolId !== null &&
          !this._isLandingSpriteVisible()
        ) {
          this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
          this.pendingLandingSymbolId = null;
        }

        if (shouldUpdateTexture) {
          this.updateSymbolTextureRandomly(symbol);
        }

        if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
          shouldExecuteStop = true;
        }
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    const sprites = this.spinningSprites;
    const h = this.symbolHeight;
    const len = sprites.length;

    for (let i = 0; i < len; i++) {
      sprites[i].container.y = i * h;
    }
  }

  // Calculates current drift from expected positions
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleStartIndex = VISIBLE_SYMBOL_START_INDEX;
    const visibleEndIndex = visibleStartIndex + VISIBLE_SYMBOL_COUNT;

    let totalDrift = 0;
    let count = 0;

    for (
      let i = visibleStartIndex;
      i < visibleEndIndex && i < this.spinningSprites.length;
      i++
    ) {
      const expectedY = i * this.symbolHeight;
      const actualY = this.spinningSprites[i].container.y;
      const drift = actualY - expectedY;
      totalDrift += drift;
      count++;
    }

    const averageDrift = count > 0 ? totalDrift / count : 0;
    return averageDrift;
  }

  // Updates sprite with random symbol
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomSymbolId();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Initializes textures for spinning sprites
  _initializeSpinningTextures() {
    this.spinningSprites.forEach((spriteData, index) => {
      const randomSymbolId = this._generateRandomSymbolId();
      this._setSpriteTexture(spriteData, randomSymbolId);
    });
    this.hasInitializedTextures = true;
  }

  // Determines stop sound based on visible symbols (wild > coin > reel_stop)
  _getReelStopSound() {
    let hasWild = false;
    let hasCoin = false;

    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      const symbolId = this.reels[i];
      if (symbolId !== undefined) {
        if (symbolId === WILD_SYMBOL_ID) {
          hasWild = true;
        } else if (COIN_SYMBOL_IDS.includes(symbolId)) {
          hasCoin = true;
        }
      }
    }

    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Determines turbo mode stop sound by checking all reels - used on last reel
  _getTurboModeSound() {
    const state = store.getState();
    if (
      !state ||
      !state.game ||
      !state.game.currentRound ||
      !state.game.currentRound.reels
    ) {
      return this._getReelStopSound();
    }

    const allReels = state.game.currentRound.reels;
    let hasWild = false;
    let hasCoin = false;

    for (let reelIndex = 0; reelIndex < allReels.length; reelIndex++) {
      const reel = allReels[reelIndex];
      if (!reel) continue;

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
        const symbolId = reel[i];
        if (symbolId !== undefined) {
          if (symbolId === WILD_SYMBOL_ID) {
            hasWild = true;
            break;
          } else if (COIN_SYMBOL_IDS.includes(symbolId)) {
            hasCoin = true;
          }
        }
      }

      if (hasWild) break;
    }

    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Sets win state for this spin - affects sound playback
  setHasWinThisSpin(hasWin) {
    this.hasWinThisSpin = !!hasWin;
  }

  // Sets Hold & Win trigger state for this spin - affects spin complete delay
  setIsHoldAndWinTrigger(isTrigger) {
    this.isHoldAndWinTrigger = !!isTrigger;
  }

  // Gets speed configuration based on locked or current autoPlaySpeed
  _getSpeedConfig() {
    const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;
    return (
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[effectiveSpeed?.toUpperCase()] ||
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO
    );
  }

  // Blur transition - animates visible sprites only
  _switchToBlurredTextures(duration = null) {
    if (!this.spritesheetBlurred) return;

    if (duration === null) {
      const speedConfig = this._getSpeedConfig();
      duration = Math.round(speedConfig.SPIN_DURATION * 0.5);
    }

    const targetsNormal = [];
    const targetsBlurred = [];

    this.visibleSprites.forEach((spriteData) => {
      if (!spriteData.blurredSprite) return;
      if (spriteData.blurredSprite.texture === PIXI.Texture.EMPTY) return;
      targetsNormal.push(spriteData.normalSprite);
      targetsBlurred.push(spriteData.blurredSprite);

      // Add text to blur animation if it has coin value
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        targetsNormal.push(spriteData.valueText);
      }
      if (
        spriteData.valueTextBlurred &&
        spriteData.valueTextBlurred.text !== ""
      ) {
        targetsBlurred.push(spriteData.valueTextBlurred);
      }
    });

    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    if (targetsNormal.length && targetsBlurred.length) {
      this.blurTweenRefs.normal = anime({
        targets: targetsNormal,
        alpha: 0.001,
        duration: duration,
        easing: "easeInOutQuad",
      });

      this.blurTweenRefs.blurred = anime({
        targets: targetsBlurred,
        alpha: 1,
        duration: duration,
        easing: "easeInOutQuad",
      });
    }

    this.spinningSprites.forEach((spriteData) => {
      if (
        !this.visibleSprites.includes(spriteData) &&
        spriteData.blurredSprite
      ) {
        spriteData.normalSprite.alpha = 0.001;
        spriteData.blurredSprite.alpha = 1;

        // Switch text to blurred for non-visible sprites too
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 0;
        }
        if (
          spriteData.valueTextBlurred &&
          spriteData.valueTextBlurred.text !== ""
        ) {
          spriteData.valueTextBlurred.alpha = 1;
        }
      }
    });

    this.isBlurred = true;
  }

  // Unblur all sprites - instant transition
  _switchToNormalTextures() {
    if (!this.spritesheetBlurred) {
      return Promise.resolve();
    }

    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;
      sprite.normalSprite.alpha = 1;
      sprite.blurredSprite.alpha = 0.001;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.spritesheetBlurred) return;

    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }
    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
    const bufferAbove = landingZoneStart - 1;
    const bufferBelow = landingZoneEnd;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      const isLandingZone = i >= landingZoneStart && i < landingZoneEnd;
      const isBufferAbove = i === bufferAbove;
      const isBufferBelow = i === bufferBelow;

      if (isLandingZone || isBufferAbove || isBufferBelow) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0.001;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    }

    this.isBlurred = false;
  }

  // Calculates stop and animates sprites to landing position
  async _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    const hasApiData = this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    if (hasApiData) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites();
      }
    } else if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const baseDuration = Math.abs(
      (adjustedLandingDistance / this.normalizedSpinSpeed) * 1000
    );
    const speedConfig = this._getSpeedConfig();
    let stopDuration = baseDuration / speedConfig.STOP_SLOWDOWN_FACTOR;

    if (this.speedMultiplier < 1.0) {
      stopDuration = stopDuration / this.speedMultiplier;
    }

    this._setLandingSymbolOnVisibleSprites();

    if (this.reels && this.reels.length > 1) {
      this.symbolId = this.reels[1];
    }

    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;

    let lastProgress = 0;

    // Animate sprites using drift-compensated distance with bounce easing
    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: adjustedLandingDistance,
      duration: stopDuration,
      easing: function () {
        return function (t, overshoot = speedConfig.BOUNCE_OVERSHOOT) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      begin: () => {
        this.isPlaying = false;
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / adjustedLandingDistance;

        if (this.stopAnimationProgress >= 0.6 && this.isBlurred) {
          this._switchToNormalTexturesSelective();
        }

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold = SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: async () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        const hadPendingResize = this._pendingPositionUpdate;
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        for (let w = 0; w < STOP_MOVE_DISTANCE_SYMBOLS; w++) {
          const movedSprite = this.spinningSprites.pop();
          this.spinningSprites.unshift(movedSprite);
        }

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }

        // Set critical state flags immediately
        this.hasStopped = true;
        this.stopTweenActive = false;
        this.spinningContainer.y = 0;
        this.lockedSpinSpeed = null;

        // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }

        // Heavy operations AFTER landing animations start (non-blocking for animations)
        if (hadPendingResize) {
          this._forceRescaleAllTextures();
        }

        if (this.isBlurred) {
          this._switchToNormalTextures();
        }

        this.spinningSprites.forEach((spriteData) => {
          spriteData.normalSprite.cacheAsBitmap = true;
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.cacheAsBitmap = true;
          }
        });

        soundManager.play(this._getReelStopSound());

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          soundManager.stop(SOUND_NAME.reel_spin);

          // Add delay for non-winning spins to prevent spam
          // Winning spins and Hold & Win triggers are protected by their respective animations
          if (this.hasWinThisSpin || this.isHoldAndWinTrigger) {
            this.dispatch(setSpinComplete(true));
          } else {
            const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;
            const isTurbo = effectiveSpeed?.toLowerCase() === 'turbo';
            const delay = isTurbo
              ? FEATURES_CONFIG.SPIN.SPIN_COMPLETE_DELAY.TURBO
              : FEATURES_CONFIG.SPIN.SPIN_COMPLETE_DELAY.REGULAR;

            const timeoutId = setTimeout(() => {
              // Remove from tracking array when it fires
              const idx = this.pendingTimeouts.indexOf(timeoutId);
              if (idx > -1) this.pendingTimeouts.splice(idx, 1);

              this.dispatch(setSpinComplete(true));
            }, delay);

            // Track timeout for cleanup
            this.pendingTimeouts.push(timeoutId);
          }
        }
      },
    });
  }

  // Forces re-scaling of all sprite textures
  _forceRescaleAllTextures() {
    if (!this.spinningSprites.length) return;

    this.spinningSprites.forEach((spriteData) => {
      if (spriteData.currentSymbolId !== null) {
        const info = symbolData[spriteData.currentSymbolId];
        if (info?.name) {
          const scale = this._calculateSymbolScale(
            info.name,
            this.symbolWidth,
            this.symbolHeight
          );
          spriteData.normalSprite.scale.set(scale);
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.scale.set(scale);
          }
        }
      }
    });
  }

  // Set landing symbol - typically called with API data during spin
  setLandingSymbol(symbolId) {
    if (symbolId === undefined || symbolId === null) {
      return;
    }

    if (this.isPlaying && this.spinningSprites.length > 0) {
      if (this._isLandingSpriteVisible()) {
        this.pendingLandingSymbolId = symbolId;
        this.symbolId = symbolId;
      } else {
        this.symbolId = symbolId;
        this.pendingLandingSymbolId = null;
        this._setLandingSymbolOnVisibleSprites(symbolId);
      }
      return;
    }

    this.symbolId = symbolId;
    this.updateSymbolTexture(symbolId);
  }

  // Applies pending resize
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    const scaleRatio = newSymbolHeight / oldSymbolHeight;
    const isSpinning = this.spinStartTime && !this.hasStopped;
    const isStopAnimating = this.stopTweenActive && this.activeStopAnimation;

    if (isStopAnimating) {
      return;
    }

    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this.nativeDimensions = null;

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      this.spinningSprites.forEach((spriteData) => {
        spriteData.container.y =
          Math.round(spriteData.container.y * scaleRatio * PRECISION_FACTOR) /
          PRECISION_FACTOR;
      });

      this.lastCalculatedPosition =
        Math.round(
          this.lastCalculatedPosition * scaleRatio * PRECISION_FACTOR
        ) / PRECISION_FACTOR;

      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.normalSprite.scale.set(scale);
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  // Update dimensions when symbolHeight changes
  resizeSymbol(newSymbolHeight) {
    this._targetSymbolHeight = newSymbolHeight;
    this._targetSymbolWidth = newSymbolHeight;

    if (
      this.symbolHeight === newSymbolHeight &&
      this.symbolWidth === this._targetSymbolWidth
    ) {
      this._pendingPositionUpdate = false;
      return;
    }

    const oldSymbolHeight = this.symbolHeight;

    // If spinning OR stopping, defer update to avoid mid-animation position chaos
    if ((this.isPlaying || this.stopTweenActive) && !this.hasStopped) {
      this._originalSymbolHeight = oldSymbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this.symbolHeight = newSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this._originalSymbolHeight = oldSymbolHeight;
    this._pendingPositionUpdate = false;
    this.nativeDimensions = null;

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            spriteData.normalSprite.scale.set(scale);
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }
  }

  // Texture Visibility Control (for overlay spine animations)

  // Hides symbol texture to show overlay spine animation
  hideSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0.001;
  }

  // Shows symbol texture (hides overlay animation)
  showSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    sprite.normalSprite.alpha = 1;
    sprite.blurredSprite.alpha = 0.001;
  }

  // Shows all visible symbol textures (clears overlay animations)
  showAllSymbolTextures() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite) {
        sprite.normalSprite.alpha = 1;
        sprite.blurredSprite.alpha = 0.001;
      }
    }
  }

  // Exposes sprites in format compatible with useWinController - returns only visible sprites
  get symbolsContainer() {
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    const visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);

    return {
      children: visibleSprites.map((sprite, overlayIndex) => ({
        currentSymbolId: sprite.currentSymbolId,
        symbolId: sprite.currentSymbolId,
        overlayIndex: overlayIndex, // Direct mapping: 0, 1, 2, 3
      })),
    };
  }

  // Expanding Wild Support

  // Replaces all visible symbols with target symbol (e.g., wild expansion)
  // @param {number} targetSymbolId - Symbol ID to replace with
  // @param {boolean} hideTextures - If true, hides textures (for cascade mode). If false, keeps visible (for full-reel mode)
  replaceVisibleSymbols(targetSymbolId, hideTextures = true) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // Replace all visible sprites
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) continue;

      sprite.normalSprite.texture = texture;
      sprite.currentSymbolId = targetSymbolId;

      // Only hide textures if requested (cascade mode needs this, full-reel mode doesn't)
      if (hideTextures) {
        sprite.normalSprite.alpha = 0.001;
        sprite.blurredSprite.alpha = 0.001;
      }
    }
  }

  // Replaces single symbol at position (for cascading wild animation)
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // CRITICAL: Update the reels array for this position
    // This prevents blinking when next spin starts
    // Create a new array since the original might be read-only (from Redux state)
    if (this.reels && this.reels.length > symbolIndex) {
      this.reels = this.reels.map((id, idx) =>
        idx === symbolIndex ? targetSymbolId : id
      );
    }

    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    sprite.normalSprite.texture = texture;
    sprite.currentSymbolId = targetSymbolId;
    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0.001;
  }

  // Cleanup

  clearAllTimeouts() {
    // Clear all pending setTimeout calls
    if (this.pendingTimeouts && this.pendingTimeouts.length > 0) {
      this.pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.pendingTimeouts = [];
    }

    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      this.activeStopAnimation = null;
    }

    if (this.blurTweenRefs.normal) {
      this.blurTweenRefs.normal.pause();
      this.blurTweenRefs.normal = null;
    }

    if (this.blurTweenRefs.blurred) {
      this.blurTweenRefs.blurred.pause();
      this.blurTweenRefs.blurred = null;
    }

    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // ignore
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

export default ReelControllerV2;
